import {
  Box,
  Button,
  getColor,
  Typography,
  InputField,
  Modal,
  Icons,
  Tooltip,
  useToast,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import FindPasswordModal from "../Login/FindPasswordModal";
import {
  getRedirectUrl,
  getUserInfoFromLocalStorage,
  getWorkspaceCode,
} from "../../shared/utils/auth";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { nanoid } from "@reduxjs/toolkit";
import ESETLoginModal from "../Login/ESETLoginModal";
import GoogleOTPLoginModal from "../Login/GoogleOTPLoginModal";
import GoogleOTPRegisterModal from "../Login/GoogleOTPRegisterModal";
import MailOTPLoginModal from "../Login/MailOTPLoginModal";
import { LoginDto } from "../../types/auth";
import { useMediaQuery } from "../../shared/hooks/useMediaQuery";
import classNames from "classnames";

const LinkLogin = ({
  changeLoading,
}: {
  changeLoading: (status: boolean) => void;
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const navigate = useNavigate();
  const interval = useRef<any>();
  const {
    loginState,
    sessionNew,
    loginSession,
    checkOTPPermission,
    sendLiteOTP,
    checkLiteOTP,
  } = useLoginStore();
  const toasts = useToast();
  const isMobile = useMediaQuery();

  /** State 정리 */
  const [email, changeEmail] = useState("");
  const [otp, changeOTP] = useState(""); // OTP
  const [pw, changePW] = useState("");
  const [counter, changeCounter] = useState(-1); // -1 일 경우 비활성, 0 보다 글경우 활성화
  const [sotp, changeSotp] = useState(""); // 권한 판단 후 otp 전달 받음
  const [step, changeStep] = useState<
    | "CHECK_EMAIL"
    | "SEND_OTP"
    | "CHECK_OTP"
    | "CHECK_PW"
    | "NO_LICENSE"
    | "NO_LICENSE_EX"
    | "NO_AUTH"
    | "DELETED"
  >("CHECK_EMAIL"); // CHECK_EMAIL: 이메일 입력, SEND_OTP: 인증코드 전송,  CHECK_OTP: 인증코드 입력, CHECK_PW: 비밀번호 입력, NO_AUTH: 사용 권한 없음, NO_LICENSE:이용 가능 라이선스 없는 경우, NO_LICENSE_EX: 이용 가능 라이선스가 없는 경우(내외부)
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE");
  const [err, changeErr] = useState("NONE");
  const [isPwVisible, togglePWVisible] = useState(false); // 비밀번호 보임, 안보임 처리용
  const extraInputInfo = {
    CHECK_EMAIL: {
      inputfield_placeholder: t("login.link.pressEmailPlaceholder"),
      inputfield_value: email,
      onchange: (e: any) => changeEmail(e.target.value),
      submit_disable: email.trim() === "",
    },
    SEND_OTP: {
      inputfield_placeholder: t("login.link.pressEmailPlaceholder"),
      inputfield_value: email,
      onchange: (e: any) => changeEmail(e.target.value),
      submit_disable: email.trim() === "",
    },
    CHECK_OTP: {
      inputfield_placeholder: isMobile
        ? t("login.link.sentOTPPlaceholderShort")
        : t("login.link.sentOTPPlaceholder"),
      inputfield_value: otp,
      onchange: (e: any) => changeOTP(e.target.value),
      submit_disable: otp.trim() === "",
    },
    CHECK_PW: {
      inputfield_placeholder: t("login.link.pressPWPlaceholder"),
      inputfield_value: pw,
      onchange: (e: any) => changePW(e.target.value),
      submit_disable: pw.trim() === "",
    },
    NO_AUTH: {
      inputfield_placeholder: "",
      inputfield_value: "",
      onchange: undefined,
      submit_disable: false,
    },
    DELETED: {
      inputfield_placeholder: "",
      inputfield_value: "",
      onchange: undefined,
      submit_disable: false,
    },
    NO_LICENSE: {
      inputfield_placeholder: "",
      inputfield_value: "",
      onchange: undefined,
      submit_disable: false,
    },
    NO_LICENSE_EX: {
      inputfield_placeholder: "",
      inputfield_value: "",
      onchange: undefined,
      submit_disable: false,
    },
  };
  /** UseEffect 정리 */
  useEffect(() => {
    if (counter === 0) {
      clearInterval(interval.current);
    }
  }, [counter]);
  useEffect(() => {
    window.sessionStorage.removeItem("signupRequestUuid");
  }, []);

  useEffect(() => {
    if (step === "CHECK_EMAIL") return;
    window.history.pushState(null, "", window.location.href);
    const blockBack = () => {
      changeStep("CHECK_EMAIL");
      changeErr("NONE");
      changeEmail("");
      changePW("");
      changeOTP("");
      changeSotp("");
      togglePWVisible(false);
      changeOpenExtraModal("CLOSE");
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", blockBack);

    return () => {
      window.removeEventListener("popstate", blockBack);
    };
  }, [step]);
  /** 함수 정리 */
  const handleLogin = async () => {
    if (!email || !pw) return;
    /** 에러: 비밀번호가 유효하지 않음. */
    const otp: any = await sessionNew();
    if (otp.payload) {
      const resp: any = await loginSession({
        userId: email,
        userPwd: pw,
        otp: otp.payload.apiToken,
      });
      if (resp.error && resp.error.message) {
        if (resp.error.name !== "INVALID_USER_ID_PWD") {
          if (resp?.error?.message.includes("MAIL_OTP")) {
            changeOpenExtraModal("MAIL_OTP");
          } else if (resp?.error?.message?.includes("GOOGLE_OTP")) {
            changeOpenExtraModal("GOOGLE_OTP_REGISTER");
          } else if (resp?.error?.name === "NOT_ALLOW_IP") {
            navigate(`${process.env.PUBLIC_URL}/error?errorCode=NOT_ALLOW_IP`);
          } else if (resp.error?.name === "INVALID_TENANCY") {
            navigate("/error?errorCode=INVALID_TENANCY");
          } else if (resp.error?.name === "SHOULD_RENEW_PASSWORD") {
            changeOpenExtraModal("CHANGE_PW");
          } else if (resp.error?.name?.includes("ESET_OTP")) {
            changeOpenExtraModal("ESET_LOGIN");
          } else if (resp.error?.name === "INVALID_USERINFO") {
            navigate(`/checkSignup?status=MAIL_SENT&userId=${email}`);
          } else if (resp.error?.name === "EXCEED_PASSWORD_ERROR_LIMIT") {
            toasts.toastMsg(
              nanoid(),
              t("login.login.error.passwordErrorLimit"),
              "error"
            );
          } else if (
            [
              "OVER_LICENSE_INTERNAL_USER_LIMIT",
              "OVER_LICENSE_EXTERNAL_USER_LIMIT",
            ].includes(resp?.error.name)
          ) {
            changeStep("NO_LICENSE_EX");
          } else {
            toasts.toastMsg(nanoid(), resp.error.message, "error");
          }
        } else {
          changeErr("INVALID_USER_ID_PWD");
        }
        return;
      }
      changeErr("NONE");
      if (resp?.payload?.multiFactor === "MAIL_OTP") {
        changeOpenExtraModal("MAIL_OTP");
      } else if (resp?.payload?.multiFactor === "GOOGLE_OTP") {
        changeOpenExtraModal("GOOGLE_OTP");
      } else if (resp.payload?.multiFactor === "ESET_OTP") {
        changeOpenExtraModal("ESET_LOGIN");
      } else if (resp.payload) {
        let redirectUrl = getRedirectUrl();
        if (redirectUrl && !redirectUrl.includes("/login")) {
          try {
            const url = new URL(redirectUrl);
            navigate(url.pathname);
          } catch (err) {
            window.location.replace(redirectUrl);
          }
        } else {
          const defaultTenancy = resp.payload?.defaultTenancy ?? "My Space";
          navigate( // navigate-skip: login flow, OCI handled manually with defaultTenancy
            loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
              ? `${process.env.PUBLIC_URL}/myInbox/received`
              : window.eco.serviceType === "OCI" && defaultTenancy
              ? `${process.env.PUBLIC_URL}/${defaultTenancy}/home`
              : `${process.env.PUBLIC_URL}/home`
          );
        }
      }
    }
  };
  const timer = () => {
    changeCounter(180);
    changeErr("NONE");
    if (counter <= 1) {
      changeCounter(180);
    }
    if (interval.current) clearInterval(interval.current);

    interval.current = setInterval(() => {
      changeCounter((counter) => counter - 1);
    }, 1000);
    return () => clearInterval(interval.current);
  };
  const handleGoNextStep = async () => {
    if (step === "CHECK_EMAIL") {
      let savedUid = window.sessionStorage.getItem("redirectUrl") ?? "";
      savedUid = savedUid?.split("/")[savedUid?.split("/").length - 1];
      let resp: any = await checkOTPPermission({
        userId: email,
        uid: savedUid,
        dsdCode: getWorkspaceCode() ?? "",
      });
      if (resp.payload) {
        const code = resp.payload?.code;
        if (code === "REGISTERED_USER_NEED_PASSWORD") changeStep("CHECK_PW");
        else if (code === "INVITED_LITE_EXTERNAL_USER_NEED_AUTH") {
          changeSotp(resp.payload.sessionUid);
          changeStep("SEND_OTP");
        } else if (code === "OVER_LICENSE_LITE_EXTERNAL_USER_LIMIT")
          changeStep("NO_LICENSE");
        else changeStep("NO_AUTH");
      } else {
        changeStep(
          resp.error?.name === "OVER_LICENSE_LITE_EXTERNAL_USER_LIMIT"
            ? "NO_LICENSE"
            : "NO_AUTH"
        );
      }
    }
    if (step === "SEND_OTP") {
      changeStep("CHECK_OTP");
    }
    if (step === "CHECK_OTP") {
      let resp: any = await checkLiteOTP({ mailOTP: otp });
      if (resp.payload) {
        changeErr("NONE");
        let redirectUrl = getRedirectUrl();
        if (redirectUrl && !redirectUrl.includes("/login")) {
          try {
            const url = new URL(redirectUrl);
            window.sessionStorage.setItem(
              "signupRequestUuid",
              resp.payload.signupRequestUuid
            );
            navigate(url.pathname);
          } catch (err) {
            window.location.replace(redirectUrl);
          }
        } else {
          const defaultTenancy = resp.payload?.defaultTenancy ?? "My Space";
          navigate( // navigate-skip: login flow, OCI handled manually with defaultTenancy
            loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
              ? `${process.env.PUBLIC_URL}/myInbox/received`
              : window.eco.serviceType === "OCI" && defaultTenancy
              ? `${process.env.PUBLIC_URL}/${defaultTenancy}/home`
              : `${process.env.PUBLIC_URL}/home`
          );
        }
      } else {
        changeErr("OTP_NO_MATCH");
      }
    }
    if (step === "CHECK_PW") {
      // 확인 후,
      handleLogin();
    }
  };

  return (
    <Box classes="HTMLLogin" direction="column">
      {openExtraModal === "findPW" && (
        <FindPasswordModal
          type="link"
          isMobile={isMobile}
          open={openExtraModal === "findPW"}
          onClose={() => changeOpenExtraModal("CLOSE")}
          changeLoading={changeLoading}
        />
      )}
      {openExtraModal === "MAIL_OTP" && (
        <MailOTPLoginModal
          open
          isMobile={isMobile}
          requestId={email}
          changeLoading={changeLoading}
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}
      {"GOOGLE_OTP_REGISTER" === openExtraModal &&
        (isMobile ? (
          <Modal
            open
            isMobile={isMobile}
            isExit={!isMobile}
            onClose={() => changeOpenExtraModal("CLOSE")}
            title={t("login.googleOTP.register")}
            bodyMsg={t("login.link.googleMobileNotice")}
            footer={
              <Box width={"100%"} alignItems="center" justifyContent="center">
                <Typography
                  type="body1_md"
                  textAlign="center"
                  exactColor={getColor("COLOR_TEXT_INTERACTIVE_BRAND")}
                  onClick={() => changeOpenExtraModal("GOOGLE_OTP")}
                >
                  {t("common.confirm")}
                </Typography>
              </Box>
            }
          />
        ) : (
          <GoogleOTPRegisterModal
            open
            requestId={email}
            onClose={() => changeOpenExtraModal("CLOSE")}
          />
        ))}
      {"GOOGLE_OTP" === openExtraModal && (
        <GoogleOTPLoginModal
          open
          isMobile={isMobile}
          requestId={email}
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}

      {openExtraModal === "ESET_LOGIN" && (
        <ESETLoginModal
          open
          userId={email}
          changeLoading={changeLoading}
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}

      {["OPEN_RESEND_OTP", "OPEN_SEND_OTP"].includes(openExtraModal) && (
        <Modal
          open={openExtraModal !== "CLOSE"}
          onClose={() => changeOpenExtraModal("CLOSE")}
          width={540}
          isMobile={isMobile}
          isExit={!isMobile}
          title={
            openExtraModal === "OPEN_SEND_OTP"
              ? t("login.link.sendSuccess")
              : t("login.link.resendSuccess")
          }
          bodyMsg={
            openExtraModal === "OPEN_SEND_OTP"
              ? t("login.link.sendSuccessDesc")
              : t("login.link.resendSuccessDesc")
          }
          footer={
            isMobile ? (
              <Box
                width="100%"
                alignItems="center"
                justifyContent="center"
                onClick={() => {
                  if (openExtraModal !== "OPEN_RESEND_OTP") {
                    handleGoNextStep();
                  }
                  changeOpenExtraModal("CLOSE");
                }}
              >
                <Typography
                  textAlign="center"
                  type="body1_md"
                  exactColor={getColor("COLOR_TEXT_BRAND")}
                >
                  {t("common.confirm")}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Button
                  label={t("common.confirm")}
                  btnStyle="fill"
                  onClick={() => {
                    // timer();
                    if (openExtraModal !== "OPEN_RESEND_OTP") {
                      handleGoNextStep();
                    }
                    changeOpenExtraModal("CLOSE");
                  }}
                />
              </Box>
            )
          }
        />
      )}
      {["NO_LICENSE_EX", "NO_LICENSE"].includes(step) ? (
        <Box
          direction="column"
          minWidth={isMobile ? "100%" : 360}
          alignItems="center"
        >
          {!isMobile && (
            <img
              src={utilsCommon.renderWrapsodyECOLogo()}
              width={136}
              alt="logo"
            />
          )}
          <img
            src={require("../../shared/images/common/noLicenseCount.png")}
            style={{ marginTop: isMobile ? 0 : "20px" }}
            height={isMobile ? 124 : undefined}
          />
          <Typography
            type={isMobile ? "d3" : "h2"}
            classes={classNames("mg", isMobile ? "mt-16" : "mt-20")}
          >
            {t("login.link.noLicense")}
          </Typography>
          <Typography
            type={isMobile ? "body2_rg" : "body1_md"}
            classes={classNames("mg", isMobile ? "mt-10" : "mt-8")}
            whiteSpace="break-spaces"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            textAlign="center"
          >
            {t("login.link.noLicenseDesc")}
          </Typography>
          <Typography
            type={isMobile ? "caption_rg" : "body2_md"}
            classes={classNames("mg", isMobile ? "mt-10" : "mt-8")}
            whiteSpace="break-spaces"
            exactColor={getColor("COLOR_TEXT_INFO")}
            textAlign="center"
          >
            {t("login.link.noLicenseReason")}
          </Typography>
          {step === "NO_LICENSE_EX" || isMobile ? (
            <Button
              label={t("login.loginDifferentAccount")}
              classes={classNames("mg", isMobile ? "mt-32" : "mt-40")}
              width={"100%"}
              style={{ minWidth: "125px", height: isMobile ? "52px" : "48px" }}
              btnStyle="fill"
              onClick={() => window.location.reload()}
            />
          ) : (
            <Button
              label={t("login.signup")}
              classes={classNames("mg", isMobile ? "mt-32" : "mt-40")}
              width={"100%"}
              style={{ minWidth: "125px", height: isMobile ? "52px" : "48px" }}
              btnStyle="fill"
              onClick={() => navigate(`${process.env.PUBLIC_URL}/checkSignup`)}
            />
          )}
        </Box>
      ) : ["NO_AUTH", "DELETED"].includes(step) ? (
        <Box
          width={"100%"}
          height={"100%"}
          alignItems="center"
          justifyContent="center"
        >
          <Box
            direction="column"
            width={"100%"}
            minWidth={"360"}
            maxWidth={360}
            justifyContent="center"
            alignItems="center"
          >
            {!isMobile && (
              <img
                src={utilsCommon.renderWrapsodyECOLogo()}
                width={136}
                alt="logo"
              />
            )}
            <img
              src={require(step === "DELETED"
                ? "../../shared/images/pages/File/trash.png"
                : "../../shared/images/common/restrictClient.png")}
              style={{ marginTop: isMobile ? 0 : "20px" }}
              height={isMobile ? 124 : undefined}
            />
            <Typography
              type={isMobile ? "d3" : "h2"}
              classes={classNames("mg", isMobile ? "mt-16" : "mt-20")}
            >
              {step === "DELETED"
                ? t("login.link.deleteFile")
                : t("login.link.noAuth")}
            </Typography>
            <Typography
              type={isMobile ? "body2_rg" : "body1_md"}
              classes={classNames("mg", isMobile ? "mt-10" : "mt-8")}
              whiteSpace="break-spaces"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              textAlign="center"
            >
              {step === "DELETED"
                ? t("login.link.deleteFileDesc")
                : t("login.link.noAuthDesc")}
            </Typography>
            <Button
              label={t("login.loginDifferentAccount")}
              classes={classNames("mg", isMobile ? "mt-32" : "mt-40")}
              width={"100%"}
              style={{ minWidth: "125px", height: isMobile ? "52px" : "48px" }}
              btnStyle="fill"
              onClick={() => window.location.reload()}
            />
            <Button
              label={t("login.link.goHome")}
              classes={classNames("mg", isMobile ? "mt-16" : "mt-20")}
              btnStyle="text"
              textColor={getColor("COLOR_TEXT_BRAND")}
              onClick={() => {
                navigate(
                  utilsCommon.renderProperNavigatePath(
                    loginDto?.ecoSystemLicenseStats?.featureAccessType ===
                      "LITE"
                      ? `/myInbox/received`
                      : "/home"
                  )
                );
              }}
              trailingIcon={
                isMobile ? undefined : (
                  <Icons
                    variant="line"
                    label="chevron"
                    direction="right"
                    stroke={getColor("COLOR_TEXT_BRAND")}
                  />
                )
              }
            />
          </Box>
        </Box>
      ) : (
        <Box direction="column" classes="getEmail" alignItems="center">
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            width={136}
            alt="logo"
          />
          <Typography
            type="h1"
            classes="HTMLLogin-title"
            style={{ marginTop: 50 }}
            textAlign="center"
            whiteSpace="break-spaces"
          >
            {step === "CHECK_EMAIL"
              ? t("login.link.pressEmail")
              : step === "SEND_OTP"
              ? t("login.link.verifyEmail")
              : step === "CHECK_OTP"
              ? t("login.link.sentOTP")
              : t("login.link.pressPW")}
          </Typography>
          {step !== "CHECK_PW" && (
            <Typography
              type="body1_md"
              whiteSpace="break-spaces"
              textAlign="center"
              maxWidth={380}
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              classes="mg mt-24"
            >
              {step === "CHECK_EMAIL"
                ? t("login.link.pressEmailDesc")
                : step === "SEND_OTP"
                ? t("login.link.typeOTP")
                : step === "CHECK_OTP"
                ? t("login.link.checkOTP")
                : ``}
            </Typography>
          )}
          <Box
            style={{ marginTop: 50 }}
            width={"100%"}
            direction={isMobile ? "column" : "row"}
          >
            <Box width={"100%"}>
              <InputField
                width={"100%"}
                placeholder={extraInputInfo[step].inputfield_placeholder}
                size="md"
                maxLength={step.includes("OTP") ? 20 : 60}
                classes="HTMLLogin-inputField"
                disabled={step === "SEND_OTP"}
                style={{ minHeight: "48px" }}
                value={extraInputInfo[step].inputfield_value}
                onChange={extraInputInfo[step].onchange}
                onKeyUp={(e) => e.code === "Enter" && handleGoNextStep()}
                type={isPwVisible || step !== "CHECK_PW" ? "text" : "password"}
                helpMessage={
                  err === "OTP_NO_MATCH"
                    ? t("login.link.unmatchOTP")
                    : counter <= 0 && step === "CHECK_OTP"
                    ? t("login.link.timeoutOTP")
                    : err === "INVALID_USER_ID_PWD"
                    ? t("login.link.invalidPW")
                    : ""
                }
                isError={
                  err !== "NONE" || (counter <= 0 && step === "CHECK_OTP")
                }
                trailingIcon={
                  step === "CHECK_OTP" ? (
                    <Typography
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_INTERACTIVE_ERROR")}
                    >
                      {counter <= 0
                        ? "00"
                        : ("" + Math.floor(counter / 60)).padStart(2, "0")}
                      :
                      {counter <= 0
                        ? "00"
                        : ("" + Math.floor(counter % 60)).padStart(2, "0")}
                    </Typography>
                  ) : step === "CHECK_EMAIL" ? (
                    email.trim()?.length > 0 && (
                      <span
                        className={"pwViewBtn mg mr-8"}
                        onClick={() => changeEmail("")}
                      >
                        <Icons
                          variant="line"
                          label="closeLine"
                          stroke={"#717985"}
                        />
                      </span>
                    )
                  ) : step === "CHECK_PW" ? (
                    <Box alignItems={"center"}>
                      {pw.trim()?.length > 0 && (
                        <span
                          className={"pwViewBtn mg mr-8"}
                          onClick={() => changePW("")}
                        >
                          <Icons
                            variant="line"
                            label="closeLine"
                            stroke={"#717985"}
                          />
                        </span>
                      )}
                      <Tooltip
                        message={
                          isPwVisible
                            ? t("login.login.hidePW")
                            : t("login.login.showPW")
                        }
                      >
                        <span
                          className={"pwViewBtn"}
                          onClick={() => togglePWVisible(!isPwVisible)}
                        >
                          <Icons
                            variant="line"
                            label="preview"
                            isOn={!isPwVisible}
                            stroke={"#717985"}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  ) : undefined
                }
              />
              {step === "CHECK_OTP" && (
                <Button
                  label={t("login.link.resend")}
                  btnStyle="fill"
                  labelTypgraphy="body2_md"
                  classes="mg ml-8"
                  type="secondary"
                  whiteSpace="pre"
                  size="md"
                  style={{ height: "48px" }}
                  state={counter > 0 ? "disable" : "default"}
                  onClick={() => {
                    timer();
                    sendLiteOTP({ userId: email, sotp: sotp });
                    changeOpenExtraModal("OPEN_RESEND_OTP");
                  }}
                />
              )}
            </Box>

            {step === "SEND_OTP" && (
              <Button
                label={t("login.link.sendOTP")}
                btnStyle="fill"
                width={isMobile ? "100%" : 125}
                classes={isMobile ? "mg mt-40" : "mg ml-8"}
                style={{ minWidth: "125px", height: "48px" }}
                onClick={() => {
                  timer();
                  sendLiteOTP({ userId: email, sotp: sotp });
                  changeOpenExtraModal("OPEN_SEND_OTP");
                }}
              />
            )}
          </Box>
          {step !== "SEND_OTP" && (
            <Button
              label={
                step === "CHECK_EMAIL"
                  ? t("common.continue")
                  : step === "CHECK_OTP"
                  ? t("login.link.verify")
                  : t("login.title")
              }
              classes="mg mt-40"
              btnStyle="fill"
              width={"100%"}
              style={{ height: "48px" }}
              onClick={handleGoNextStep}
              state={
                extraInputInfo[step].submit_disable ? "disable" : "default"
              }
            />
          )}
          {step !== "CHECK_EMAIL" && (
            <Box direction="column" alignItems="center">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                style={{ marginTop: 50 }}
              >
                {step === "CHECK_PW"
                  ? t("login.link.forgotPW")
                  : t("login.link.signupDesc")}
              </Typography>
              <Button
                label={
                  step === "CHECK_PW"
                    ? t("login.link.forgotPWBtn")
                    : t("wsLogin.signup")
                }
                size="lg"
                width={"fit-content"}
                trailingIcon={
                  <Icons
                    variant="line"
                    label="chevron"
                    direction="right"
                    stroke={getColor("COLOR_ICON_BRAND")}
                  />
                }
                onClick={() =>
                  step === "CHECK_PW"
                    ? changeOpenExtraModal("findPW")
                    : navigate(`${process.env.PUBLIC_URL}/checkSignup`)
                }
                btnStyle="link"
                classes="mg mt-10"
                textColor={getColor("COLOR_TEXT_BRAND")}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LinkLogin;
